<template>
  <div class="relative">
    <input
      type="text"
      :value="displayValue"
      :placeholder="placeholder"
      @click="openFilter"
      @input="handleFilterInput"
      @blur="closeAfterDelay"
      @keydown.escape="isOpen = false"
      @keydown.enter="selectFirstOption"
      class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
    />
    <div
      v-if="isOpen"
      class="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-20 max-h-48 overflow-y-auto"
    >
      <template v-if="loadOptions">
        <div
          v-for="option in remoteBaseOptions"
          :key="'b-' + String(option.value)"
          @mousedown.prevent="selectOption(option.value)"
          class="px-4 py-2 hover:bg-indigo-50 cursor-pointer text-gray-900"
        >
          {{ option.label }}
        </div>
        <div v-if="remoteHint" class="px-4 py-2 text-sm text-gray-500">
          {{ remoteHint }}
        </div>
        <div v-else-if="remoteLoading" class="px-4 py-2 text-gray-500">
          Buscando...
        </div>
        <template v-else>
          <div
            v-for="option in remoteOptions"
            :key="'r-' + String(option.value)"
            @mousedown.prevent="selectOption(option.value)"
            class="px-4 py-2 hover:bg-indigo-50 cursor-pointer text-gray-900"
          >
            {{ option.label }}
          </div>
          <div v-if="showRemoteEmpty" class="px-4 py-2 text-gray-500">
            Nenhuma opção encontrada
          </div>
        </template>
      </template>
      <template v-else>
        <div
          v-for="option in filteredOptions"
          :key="String(option.value)"
          @mousedown.prevent="selectOption(option.value)"
          class="px-4 py-2 hover:bg-indigo-50 cursor-pointer text-gray-900"
        >
          {{ option.label }}
        </div>
        <div v-if="filteredOptions.length === 0" class="px-4 py-2 text-gray-500">
          Nenhuma opção encontrada
        </div>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    options: {
      type: Array,
      required: true
    },
    /** Se definido, opções da lista são carregadas via API conforme o texto (debounce). */
    loadOptions: {
      type: Function,
      default: null
    },
    minRemoteLength: {
      type: Number,
      default: 2
    },
    remoteDebounceMs: {
      type: Number,
      default: 350
    },
    placeholder: {
      type: String,
      default: 'Selecione ou digite para filtrar...'
    }
  },

  emits: ['update:modelValue'],

  data() {
    return {
      isOpen: false,
      filterText: '',
      remoteOptions: [],
      remoteLoading: false,
      remoteDebounceId: null
    }
  },

  computed: {
    trimmedFilter() {
      return (this.filterText || '').trim()
    },

    remoteBaseOptions() {
      const opts = Array.isArray(this.options) ? this.options : []
      return opts.filter((o) => o && String(o.value) === '')
    },

    remoteHint() {
      if (!this.loadOptions || !this.isOpen) return ''
      const q = this.trimmedFilter
      if (q.length > 0 && q.length < this.minRemoteLength) {
        return `Digite pelo menos ${this.minRemoteLength} caracteres para buscar.`
      }
      return ''
    },

    showRemoteEmpty() {
      if (!this.loadOptions || !this.isOpen || this.remoteLoading || this.remoteHint) return false
      return this.trimmedFilter.length >= this.minRemoteLength && this.remoteOptions.length === 0
    },

    displayValue() {
      if (this.isOpen) {
        return this.filterText
      }
      if (!Array.isArray(this.options)) {
        return this.loadOptions && this.modelValue ? this.modelValue : ''
      }
      const selected = this.options.find((opt) => String(opt.value) === String(this.modelValue))
      if (selected) return selected.label
      if (this.loadOptions && this.modelValue) return this.modelValue
      return ''
    },

    filteredOptions() {
      const opts = Array.isArray(this.options) ? this.options : []
      const newOption = opts.find((o) => o && String(o.value) === 'new')
      const others = opts.filter((o) => !o || String(o.value) !== 'new')

      let filteredOthers = others
      if (this.filterText) {
        const searchLower = this.filterText.toLowerCase()
        filteredOthers = others.filter((opt) => {
          const label = (opt.label || '').toString().toLowerCase()
          const value = (opt.value || '').toString().toLowerCase()
          return label.includes(searchLower) || value.includes(searchLower)
        })
      }

      const result = []
      if (newOption) result.push(newOption)
      return result.concat(filteredOthers)
    }
  },

  watch: {
    filterText() {
      this.scheduleRemoteLoad()
    }
  },

  beforeUnmount() {
    if (this.remoteDebounceId) {
      clearTimeout(this.remoteDebounceId)
      this.remoteDebounceId = null
    }
  },

  methods: {
    scheduleRemoteLoad() {
      if (!this.loadOptions || !this.isOpen) return
      if (this.remoteDebounceId) clearTimeout(this.remoteDebounceId)
      this.remoteDebounceId = setTimeout(() => {
        this.remoteDebounceId = null
        this.runRemoteLoad()
      }, this.remoteDebounceMs)
    },

    async runRemoteLoad() {
      if (!this.loadOptions || !this.isOpen) return
      const q = this.trimmedFilter
      if (q.length < this.minRemoteLength) {
        this.remoteOptions = []
        this.remoteLoading = false
        return
      }
      this.remoteLoading = true
      try {
        const list = await this.loadOptions(q)
        this.remoteOptions = Array.isArray(list) ? list : []
      } catch (e) {
        console.error('SelectWithFilter loadOptions:', e)
        this.remoteOptions = []
      } finally {
        this.remoteLoading = false
      }
    },

    openFilter() {
      this.isOpen = true
      this.remoteOptions = []
      this.$nextTick(() => {
        const input = this.$el.querySelector('input')
        if (input) input.focus()
        this.scheduleRemoteLoad()
      })
    },

    handleFilterInput(event) {
      this.filterText = event.target.value
      this.isOpen = true
    },

    selectOption(value) {
      this.$emit('update:modelValue', value)
      this.isOpen = false
      this.filterText = ''
      this.remoteOptions = []
    },

    selectableRemoteRows() {
      if (this.loadOptions) {
        const rows = [...this.remoteBaseOptions]
        if (!this.remoteHint && !this.remoteLoading) {
          rows.push(...this.remoteOptions)
        }
        return rows
      }
      return this.filteredOptions
    },

    selectFirstOption() {
      const rows = this.selectableRemoteRows()
      if (rows.length > 0) {
        this.selectOption(rows[0].value)
      }
    },

    closeAfterDelay() {
      setTimeout(() => {
        if (!this.isOpen) return
        this.isOpen = false
        this.filterText = ''
        this.remoteOptions = []
      }, 200)
    }
  }
}
</script>
